// Detect rendering capabilities, motion preferences, and device type
// WebGPURenderer requires at least WebGL2 — it auto-falls back from WebGPU to WebGL2

export interface RenderCapabilities {
  webgl2: boolean;
  reducedMotion: boolean;
  isMobile: boolean;
  pixelRatio: number;
  qualityLevel: 1 | 2 | 3 | 4;
}

export const QUALITY_BUDGETS = {
  1: { maxParticles: 0, bloomEnabled: false, bloomStrength: 0, bloomRadius: 0, bloomThreshold: 1, vignetteEnabled: false, gridMinorLines: false, gridPulse: false, maxPixelRatio: 1 },
  2: { maxParticles: 200, bloomEnabled: false, bloomStrength: 0, bloomRadius: 0, bloomThreshold: 1, vignetteEnabled: false, gridMinorLines: false, gridPulse: false, maxPixelRatio: 1 },
  3: { maxParticles: 500, bloomEnabled: true, bloomStrength: 1.0, bloomRadius: 0.35, bloomThreshold: 0.25, vignetteEnabled: true, gridMinorLines: true, gridPulse: true, maxPixelRatio: 1.5 },
  4: { maxParticles: 1000, bloomEnabled: true, bloomStrength: 1.5, bloomRadius: 0.5, bloomThreshold: 0.15, vignetteEnabled: true, gridMinorLines: true, gridPulse: true, maxPixelRatio: 2 },
} as const;

function detectQualityLevel(gl2: WebGL2RenderingContext | null, isMobile: boolean): 1 | 2 | 3 | 4 {
  if (!gl2) return 1;

  if (isMobile) return 2;

  // Try to identify GPU via debug renderer info
  const debugInfo = gl2.getExtension('WEBGL_debug_renderer_info');
  if (debugInfo) {
    const renderer = gl2.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();

    // Dedicated GPU → Level 4
    if (/nvidia|geforce|radeon|amd|ati/.test(renderer)) return 4;

    // Known integrated GPU → Level 3
    if (/intel|apple|mali|adreno|powervr|vivante/.test(renderer)) return 3;
  }

  // Unknown → safe default
  return 3;
}

export function detectCapabilities(): RenderCapabilities {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.maxTouchPoints > 1 && window.innerWidth < 1024);

  // WebGPURenderer needs at least WebGL2 as fallback
  const canvas = document.createElement('canvas');
  const gl2 = canvas.getContext('webgl2');
  const webgl2 = !!gl2;

  const qualityLevel = detectQualityLevel(gl2, isMobile);
  const maxRatio = QUALITY_BUDGETS[qualityLevel].maxPixelRatio;
  const pixelRatio = Math.min(window.devicePixelRatio || 1, maxRatio);

  if (gl2) {
    gl2.getExtension('WEBGL_lose_context')?.loseContext();
  }

  return { webgl2, reducedMotion, isMobile, pixelRatio, qualityLevel };
}
